import {useCallback, useEffect, useState} from 'react';
import type {ChangeEvent, ClipboardEvent} from 'react';

import {attachmentProblem} from './attachmentFile';

/**
 * Выбранный файл до отправки: сам файл, миниатюра и разбор отказа (SKS-124).
 *
 * Один хук на композер переписки и на форму обращения. Обе формы держат файл
 * до нажатия «Отправить», показывают миниатюру и отказывают по формату на месте,
 * и это ровно тот код, который разъезжается молча: копия в одной форме отзывала бы
 * object URL, а в другой нет, или проверяла бы формат по-своему.
 *
 * Файл попадает сюда двумя путями — скрепкой и вставкой из буфера (SKS-336), —
 * и оба идут через одну проверку. Вставка нужна скриншоту: он живёт в буфере,
 * и сохранять его на диск, чтобы потом найти в проводнике, — лишние полминуты
 * на каждом обращении.
 *
 * Миниатюра берётся из самого файла через object URL: до отправки он существует
 * только в браузере, и показать его больше нечем.
 */
export function useAttachmentPick(onProblem: (message: string | null) => void): {
	file: File | null;
	/** Object URL картинки или null: у PDF и документа предпросмотра нет. */
	preview: string | null;
	/** Поставить или снять файл вручную — «×» и очистка после успешной отправки. */
	pick: (next: File | null) => void;
	onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
	/** Вставка картинки из буфера — на поле ввода текста. */
	onPaste: (event: ClipboardEvent<HTMLElement>) => void;
} {
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<string | null>(null);

	// Уборка в эффекте, а не в обработчике выбора: тогда она срабатывает и на
	// замене файла, и на отправке, и на размонтировании
	useEffect(() => {
		if (!preview) {
			return;
		}

		return () => URL.revokeObjectURL(preview);
	}, [preview]);

	const pick = useCallback((next: File | null) => {
		setFile(next);
		setPreview(next && next.type.startsWith('image/') ? URL.createObjectURL(next) : null);
	}, []);

	/** Проверить и поставить файл — общее у скрепки и вставки. */
	const accept = useCallback(
		(next: File) => {
			const problem = attachmentProblem(next);

			// Неподдерживаемый файл в форму не попадает: иначе человек дописывает
			// подпись, жмёт «Отправить» и только тогда узнаёт, что формат не тот
			if (problem) {
				onProblem(problem);

				return;
			}

			onProblem(null);
			pick(next);
		},
		[onProblem, pick],
	);

	const onFileChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const next = event.target.files?.[0] ?? null;

			// Сбрасываем сразу: повторный выбор того же файла иначе не даст события change
			event.target.value = '';

			if (next) {
				accept(next);
			}
		},
		[accept],
	);

	const onPaste = useCallback(
		(event: ClipboardEvent<HTMLElement>) => {
			const image = pastedImage(event.clipboardData);

			if (!image) {
				return;
			}

			// Текст в поле не вставляем: в буфере была картинка, и браузер иначе
			// дописал бы в сообщение её подпись или путь к файлу
			event.preventDefault();
			accept(image);
		},
		[accept],
	);

	return {file, preview, pick, onFileChange, onPaste};
}

/**
 * Картинка из буфера или null, если вставляют текст.
 *
 * Текст побеждает картинку. Ячейки из Excel и абзац из Word кладут в буфер и текст,
 * и его отрисовку картинкой — человек, вставляющий таблицу, ждёт таблицу в поле,
 * а не скриншот во вложении. Скриншот и «Копировать изображение» текста не несут.
 */
function pastedImage(data: DataTransfer): File | null {
	if (data.getData('text/plain').trim()) {
		return null;
	}

	const item = [...data.items].find(
		(entry) => entry.kind === 'file' && entry.type.startsWith('image/'),
	);
	const image = item?.getAsFile();

	return image ? withReadableName(image) : null;
}

/**
 * Скриншот из буфера у браузера называется «image.png» — а в переписке по имени
 * файла его ищут и поддержка, и сам человек. Имя с моментом вставки отличает
 * два скриншота одного обращения друг от друга.
 */
function withReadableName(image: File): File {
	if (image.name && !/^image\.\w+$/i.test(image.name)) {
		return image;
	}

	const extension = image.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
	const now = new Date();
	const pad = (value: number) => String(value).padStart(2, '0');
	const stamp =
		`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
		`${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

	return new File([image], `Скриншот ${stamp}.${extension}`, {type: image.type});
}
